import Search from '@/src/components/Antd/Search/Search';
import { getCollectionList } from '@/src/request/salesChannel/collection';
import { SearchOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Button, ModalFooter, SizeFittingTable } from 'src/components/Antd';
import Tooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import ModalHoc from 'src/constrains/ModalHOC';
import { useI18n } from 'src/hooks/useRouterChange';
import Actions from 'src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCheckout/Components/TableActions';
import { generalSort } from 'src/tools/helper';
import { i18nGet } from 'src/tools/tools';
import { EnumTablePosition } from 'src/types/common/common';
import css from './css.module.less';

const CollectionTable = ModalHoc(
  (props) => {
    const i18n = useI18n();
    const getI18n = (str) => {
      return i18nGet(i18n, str);
    };
    const { value, onChange, id, tableId, modalOp, disabled } = props;
    const { setShowModal, setModalTitle, setModalContent, onModalCancel } =
      modalOp || {};

    const [collections, setCollections] = React.useState(value || []);
    const isNoData = (collections || []).length === 0;
    React.useEffect(() => {
      if (value !== collections) {
        setCollections(value || []);
      }
    }, [value]);

    const handleOnChange = (att) => {
      if (onChange) {
        onChange(att);
      }
    };

    const handleOnDelete = (index) => {
      const _collections = [...collections];
      _collections.splice(index, 1);
      setCollections(_collections);
      handleOnChange(_collections);
    };

    const handleOnDeleteAll = () => {
      setCollections([]);
      handleOnChange([]);
    };

    const handleOnModalConfirm = (collections) => {
      setCollections(collections);
      handleOnChange(collections);
      onModalCancel();
    };

    const handleOnEdit = () => {
      setModalContent(
        <ModalBody
          selected={collections}
          onCancel={onModalCancel}
          onConfirm={handleOnModalConfirm}
        />,
      );
      setShowModal(true);
      setModalTitle(
        getI18n(
          'salesChannelDirectDiscount.eligibalCondition.selectCollection',
        ),
      );
    };

    return (
      <div className={css.collectionTableContainer} id={id}>
        <SizeFittingTable
          id={tableId || 'SalesChannelCollectionTable' + id}
          dataSource={collections}
          pagination={false}
          columns={col(handleOnDelete, disabled, { getI18n })}
          rowKey={'uuid'}
          scroll={{ x: 'max-content', y: 600 }}
        />
        <div className={css.btnsContainer}>
          <Button
            className={css.addBtn}
            type='primary'
            onClick={handleOnEdit}
            disabled={disabled}>
            {getI18n(
              'salesChannelDirectDiscount.eligibalCondition.selectCollection',
            )}
          </Button>
          {!isNoData && (
            <Button
              className={css.deleteBtn}
              onClick={handleOnDeleteAll}
              disabled={disabled}>
              {getI18n('general.deleteall')}
            </Button>
          )}
        </div>
      </div>
    );
  },
  { width: 900 },
);

export default CollectionTable;

const col = (onDelete, disabled, { getI18n }) => {
  return [
    {
      minWidth: 640,
      title: getI18n('general.collectionName'),
      key: 'collectionName',
      sorter: (a, b) => {
        const name1 = a.collectionName || '';
        const name2 = b.collectionName || '';
        return generalSort(name1, name2);
      },
      default: true,
      childGenerate: (a, record, index) => {
        const collectionName = get(record, 'collectionName');
        return (
          <div className={css.tableProductName}>
            <NoWrapContainer>
              <Tooltip title={collectionName}>{collectionName}</Tooltip>
            </NoWrapContainer>
          </div>
        );
      },
    },
    disabled
      ? {}
      : {
          titleGenerate: () =>
            getI18n(
              'general.advancedFilters.commonStoreProductsWithTable.actions',
            ),
          position: EnumTablePosition.mid,
          key: 'Actions',
          default: true,
          childGenerate: (url, record, index) => {
            return (
              <Actions
                onDelete={() => {
                  if (onDelete) {
                    onDelete(index);
                  }
                }}
              />
            );
          },
          width: 190,
        },
  ];
};

const ModalBody = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { uuid }: any = useParams();
  const { selected, onCancel, onConfirm } = props;
  const currentSelected = (selected || []).map((i) => i.uuid);
  const [allCollections, setAllCollections] = React.useState([]);
  const [search, setSearch] = React.useState('');

  const _data = React.useMemo(() => {
    if (search) {
      return allCollections.filter(
        (i) => get(i, 'collectionName').indexOf(search) !== -1,
      );
    }
    return allCollections;
  }, [search, allCollections]);
  const [selectedCollections, setSelectedCollections] =
    React.useState(currentSelected);

  React.useEffect(() => {
    getCollectionList(
      { salesChannelUuid: uuid },
      { salesChannelMultiLanguageRequired: true },
    ).then((i) => {
      const { summaryList } = i;
      setAllCollections(summaryList);
    });
  }, []);

  const rowSelection = {
    selectedRowKeys: selectedCollections,
    onChange: setSelectedCollections,
  };

  const handleOnOk = () => {
    const items = selectedCollections.reduce((pre, i) => {
      const tC = allCollections.find((item) => item.uuid === i);
      if (!tC) {
        return pre;
      }
      return [...pre, tC];
    }, []);
    if (onConfirm) {
      onConfirm(items);
    }
  };

  return (
    <React.Fragment>
      <Search
        maxLength={30}
        className={`${css.search || ''}`}
        placeholder={getI18n('general.filterGroup.search')}
        allowClear
        enterButton={getI18n('general.filterGroup.search')}
        prefix={<SearchOutlined />}
        onSearch={setSearch}
        onPressEnter={(e) => {
          const search = e.target.value;
          setSearch(search);
        }}
        // onPressEnter={handleOnPress}
        // onChange={setInput}
        // value={input}
        // onSearch={handleOnSearch}
      />
      <div className={css.collectionTableWrapper}>
        <SizeFittingTable
          className={css.modalTable}
          id={'SalesChannelCollectionModalTable'}
          dataSource={_data}
          pagination={false}
          rowSelection={rowSelection}
          columns={modalCol({ getI18n })}
          rowKey={'uuid'}
          scroll={{ x: 'max-content', y: 392 }}
        />
      </div>
      <ModalFooter
        frontCountTip={`${(selectedCollections || []).length} ${getI18n(
          'general.itemsselected',
        )}`}
        okText={getI18n('general.linkModule.Modal.add')}
        cancelText={getI18n('general.cancel')}
        // okClassName={css.okBtn}
        onOk={handleOnOk}
        onCancel={onCancel}
      />
    </React.Fragment>
  );
};
const modalCol = ({ getI18n }) => [
  {
    minWidth: 640,
    title: getI18n('general.collectionName'),
    key: 'collectionName',
    // className: css.collectionModalTableRow,
    default: true,
    sorter: (a, b) => {
      const name1 = a.collectionName || '';
      const name2 = b.collectionName || '';
      return generalSort(name1, name2);
    },
    childGenerate: (a, record, index) => {
      const collectionName = get(record, 'collectionName');
      return (
        <div className={css.collectionModalTableRow}>
          <NoWrapContainer>
            <Tooltip title={collectionName}>{collectionName}</Tooltip>
          </NoWrapContainer>
        </div>
      );
    },
  },
];
